import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "utils/baseQuery";
import { METHODS } from "config";

interface AdminUser {
  _id: string;
  firstName: string;
  lastName: string;
  username: string;
  mobile: string;
  gender: number;
  birthdate: string;
  job: string;
  roles: string[];
  status: number;
  avatar?: string;
  leavePerMinutes: number;
  resetLeaveMonth: number;
  createdAt: string;
  updatedAt: string;
}

interface UserListQuery {
  page?: number;
  limit?: number;
  search?: string;
  status?: number;
}

interface UserListResponse {
  data: AdminUser[];
  count: number;
  totalPages: number;
  currentPage: number;
}

interface CreateUserInput {
  firstName: string;
  lastName: string;
  username: string;
  mobile: string;
  gender: number;
  birthdate: string;
  password: string;
  job: string;
  roles: string[];
  leavePerMinutes: number;
  resetLeaveMonth: number;
}

interface EditUserInput {
  _id: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  mobile?: string;
  gender?: number;
  birthdate?: string;
  password?: string;
  job?: string;
  roles?: string[];
  leavePerMinutes?: number;
  resetLeaveMonth?: number;
}

interface UserDetailResponse {
  user: AdminUser;
  leaveData: any;
}

const AdminUserApi = createApi({
  reducerPath: "admin-user-api",
  baseQuery: baseQuery(),
  tagTypes: ["AdminUsers"],
  endpoints: (builder) => ({
    getAdminUserList: builder.query<UserListResponse, UserListQuery>({
      query: (params) => ({
        url: "/admin/user/list",
        method: METHODS.get,
        params,
      }),
      providesTags: ["AdminUsers"],
    }),
    getAdminUserDetail: builder.query<UserDetailResponse, string>({
      query: (userID) => ({
        url: "/admin/user/detail",
        method: METHODS.get,
        params: { userID },
      }),
      providesTags: ["AdminUsers"],
    }),
    createAdminUser: builder.mutation<{ user: AdminUser }, CreateUserInput>({
      query: (body) => ({
        url: "/admin/user/create",
        method: METHODS.post,
        body,
      }),
      invalidatesTags: ["AdminUsers"],
    }),
    editAdminUser: builder.mutation<{ user: AdminUser }, EditUserInput>({
      query: (body) => ({
        url: "/admin/user/edit",
        method: METHODS.post,
        body,
      }),
      invalidatesTags: ["AdminUsers"],
    }),
  }),
});

export const {
  useGetAdminUserListQuery,
  useGetAdminUserDetailQuery,
  useCreateAdminUserMutation,
  useEditAdminUserMutation,
} = AdminUserApi;

export { AdminUserApi };
export type { AdminUser, UserListQuery, CreateUserInput, EditUserInput };
